import { useEffect, useState } from "react";
import { useAppDispatch } from "../../app/hook";
import { useNavigate } from "react-router-dom";
import {
  Button,
  SpaceBetween,
  ColumnLayout,
  Form,
  Box,
  Container,
  Header,
  FormField,
  Input,
  TextContent,
} from "@cloudscape-design/components-themed";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { IndividualCounselingProjectRegister } from "../../features/api-interfaces";
import {
  isNull,
  isLengthFixedValid,
  isHalfNumberValid,
} from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import {
  usePostIndividualCounselingProjectRegisterMutation,
  useGetCorpInfoByCorpNumQuery,
} from "../../features/api";
import { Tab } from "./Tab";

export const IndividualCounselingProjectForm = (props: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [postIndividualCounselingProject] =
    usePostIndividualCounselingProjectRegisterMutation();

  // データをセット
  let IndividualCounselingProjectCompanyData: IndividualCounselingProjectRegister =
    props.IndividualCounselingProjectCompanyData;

  // 登録画面か更新画面かのフラグ
  const isRegister = IndividualCounselingProjectCompanyData ? false : true;

  //戻るボタンの表示確認フラグ（入力フォームに何か入力された場合はtrue）
  const [isFormEntered, setIsFormEntered] = useState(false);

  //登録ボタンの活性化フラグ
  const [isRegistrationButton, setIsRegistrationButton] = useState(true);

  //基本情報
  const [
    IndividualCounselingProjectRegister,
    setIndividualCounselingProjectRegister,
  ] = useState({
    corporateNumber: "",
    companyName: "",
  } as IndividualCounselingProjectRegister);

  const [corporateData, setCorporateData] = useState({
    corporateNumber: "",
    companyName: "",
  } as IndividualCounselingProjectRegister);

  //法人番号検索条件を初期化する
  const [searchInput, setSearchInput] = useState("");
  //法人番号検索結果取得API
  const { data } = useGetCorpInfoByCorpNumQuery(searchInput, {
    skip: !searchInput,
  });

  //新規の場合、法人番号押下後取得してきた事業者名データをセット
  useEffect(() => {
    if (data) {
      if (data.companyInfo === null) {
        setCorporateData({
          ...corporateData,
          companyName: "",
        });
        return;
      }

      setCorporateData({
        ...corporateData,
        companyName: data.companyInfo.companyName,
      });
      setIsRegistrationButton(false);
    }
    //検索条件を初期化する
    setSearchInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //更新の場合、DBに登録済みのデータを取得する
  useEffect(() => {
    if (IndividualCounselingProjectCompanyData !== undefined) {
      setIndividualCounselingProjectRegister(
        IndividualCounselingProjectCompanyData
      );
    }
  }, [IndividualCounselingProjectCompanyData]);

  //バリデーションチェック
  // 法人番号
  const [corporateNumberErrorMsg, setCorporateNumberErrorMsg] = useState("");
  const checkCorporateNumber = (target: string) => {
    // 必須
    if (isNull(target)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 半角数値
    if (!isHalfNumberValid(target)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR006").label);
      return false;
    }
    setCorporateNumberErrorMsg("");
    return true;
  };

  // 登録処理
  const IndividualCounselingProjectRegist = () => {
    //バリデーションチェック
    if (!checkCorporateNumber(corporateData.corporateNumber)) {
      return;
    }
    // 登録処理API呼び出し
    postIndividualCounselingProject(corporateData).then((result: any) => {
      if (!("error" in result) && "error" !== result.data.status) {
        navigate(
          "/individual-counseling-project/" +
            result.data.individualConsultationNo
        );
      }
    });
  };

  //検索ボタン押下時処理
  const onCorporateNumberSearchButton = () => {
    //バリデーションチェック
    if (!checkCorporateNumber(corporateData.corporateNumber)) {
      return;
    }
    // 文字数固定値
    if (!isLengthFixedValid(corporateData.corporateNumber, 13)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR073", 13).label);
      return;
    }
    setCorporateNumberErrorMsg("");
    setSearchInput(corporateData.corporateNumber);
  };

  // 登録ボタン押下イベント
  const onClickRegisterFormButton = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          IndividualCounselingProjectRegist();
        },
      })
    );
  };

  //戻るボタン押下時のダイアログ
  const returnToList = () => {
    if (!isFormEntered) {
      navigate("/individual-counseling-project");
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate("/individual-counseling-project");
          },
        })
      );
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <SpaceBetween direction="vertical" size="l">
        <Form>
          <SpaceBetween direction="vertical" size="l">
            <Container
              header={
                <ColumnLayout columns={2}>
                  <Box float="left">
                    <Header variant="h2">{LABELS.TITLE}</Header>
                  </Box>
                </ColumnLayout>
              }
            >
              {isRegister && (
                <SpaceBetween direction="vertical" size="l">
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">{LABELS.CORPORATE_NUMBER}</Box>
                        <Box variant="small" color="text-status-error">
                          {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                        </Box>
                      </SpaceBetween>
                    }
                    stretch={true}
                    errorText={corporateNumberErrorMsg}
                    constraintText="半角数字13桁"
                  >
                    <SpaceBetween direction="horizontal" size="l">
                      <Input
                        value={corporateData.corporateNumber}
                        onChange={(event) => {
                          setCorporateData({
                            ...corporateData,
                            corporateNumber: event.detail.value,
                          });
                          checkCorporateNumber(event.detail.value);
                          setIsFormEntered(true);
                          setIsRegistrationButton(true);
                        }}
                      />
                      <Button
                        formAction="none"
                        variant="normal"
                        onClick={onCorporateNumberSearchButton}
                      >
                        {LABELS.SEARCH}
                      </Button>
                    </SpaceBetween>
                  </FormField>
                  <SpaceBetween direction="vertical" size="l">
                    <Box variant="strong">{LABELS.COMPANY_NAME}</Box>
                    <TextContent>
                      <p>{corporateData.companyName}</p>
                    </TextContent>
                  </SpaceBetween>
                </SpaceBetween>
              )}

              {!isRegister && (
                <SpaceBetween direction="horizontal" size="xs">
                  <SpaceBetween size="xxs">
                    <TextContent>
                      <h4>{LABELS.INDIVIDUAL_NO}</h4>
                    </TextContent>
                    <TextContent>
                      <h4>{LABELS.CORPORATE_NUMBER}</h4>
                    </TextContent>
                    <TextContent>
                      <h4>{LABELS.COMPANY_NAME}</h4>
                    </TextContent>
                  </SpaceBetween>
                  <SpaceBetween size="xxxs">
                    <TextContent>
                      <p>{props.individualConsultationNo}</p>
                    </TextContent>
                    <TextContent>
                      <p>
                        {IndividualCounselingProjectRegister.corporateNumber}
                      </p>
                    </TextContent>
                    <TextContent>
                      <p>{IndividualCounselingProjectRegister.companyName}</p>
                    </TextContent>
                  </SpaceBetween>
                </SpaceBetween>
              )}
            </Container>
          </SpaceBetween>
        </Form>

        {!isRegister && (
          <Tab
            IndividualCounselingProjectContractData={
              props.IndividualCounselingProjectContractData
            }
            isFetching={props.isFetching}
          />
        )}

        <ColumnLayout columns={2}>
          <Box float="left">
            <Button formAction="none" variant="link" onClick={returnToList}>
              {COMMON_LABELS.BTNS.BACK}
            </Button>
          </Box>
          {isRegister && (
            <Box float="right">
              <Button
                onClick={onClickRegisterFormButton}
                variant="primary"
                disabled={isRegistrationButton}
              >
                {COMMON_LABELS.BTNS.REGISTER}
              </Button>
            </Box>
          )}
        </ColumnLayout>
      </SpaceBetween>
    </form>
  );
};
